/**
 * Overflow d’un groupe de pills : une seule ligne, surplus dans un menu « ⋯ ».
 * Le menu est en `position: fixed` (attaché au body) pour ne pas être clipé
 * par les `overflow: hidden` du garage-shell.
 */

export type PillOverflowPick = {
  visible: number[];
  overflow: number[];
};

/**
 * Décide quels indices restent en ligne. Si tout tient, pas de ⋯.
 * Sinon on réserve `overflowBtnWidth` et on remplit de gauche à droite.
 */
export function pickVisiblePillIndices(
  buttonWidths: number[],
  availableWidth: number,
  overflowBtnWidth: number
): PillOverflowPick {
  if (buttonWidths.length === 0) return { visible: [], overflow: [] };

  const total = buttonWidths.reduce((a, b) => a + b, 0);
  if (total <= availableWidth) {
    return { visible: buttonWidths.map((_, i) => i), overflow: [] };
  }

  const budget = Math.max(0, availableWidth - overflowBtnWidth);
  const visible: number[] = [];
  let used = 0;
  for (let i = 0; i < buttonWidths.length; i++) {
    const w = buttonWidths[i];
    if (visible.length === 0 || used + w <= budget) {
      visible.push(i);
      used += w;
    } else {
      break;
    }
  }
  const overflow = buttonWidths.map((_, i) => i).filter((i) => !visible.includes(i));
  return { visible, overflow };
}

function pillLabel(btn: HTMLElement): string {
  return (
    btn.getAttribute('aria-label')?.trim() ||
    btn.querySelector('.btn-pill-inner-static')?.textContent?.trim() ||
    btn.textContent?.trim() ||
    'Action'
  );
}

function isPillParticipating(btn: HTMLElement): boolean {
  if (btn.classList.contains('btn-pill-group__more')) return false;
  if (
    btn.dataset.role &&
    btn.style.display === 'none' &&
    !btn.classList.contains('btn-pill-group__overflowed')
  ) {
    return false;
  }
  return true;
}

/** Active l’overflow responsive sur un `.btn-pill-group` déjà peuplé. Retourne un cleanup. */
export function attachPillGroupOverflow(group: HTMLElement): () => void {
  group.classList.add('btn-pill-group--overflow');

  const overflowWrap = document.createElement('div');
  overflowWrap.className = 'btn-pill-group__overflow';
  overflowWrap.hidden = true;

  const moreBtn = document.createElement('button');
  moreBtn.type = 'button';
  moreBtn.className = 'btn-pill btn-pill--soft btn-pill-group__more';
  moreBtn.setAttribute('aria-label', 'Plus d’actions');
  moreBtn.setAttribute('aria-haspopup', 'menu');
  moreBtn.setAttribute('aria-expanded', 'false');
  moreBtn.innerHTML = `
    <span class="btn-pill-bg" aria-hidden="true">
      <span class="btn-pill-bg-layers">
        <span class="btn-pill-bg-layer btn-pill-bg-layer-1 -deep"></span>
        <span class="btn-pill-bg-layer btn-pill-bg-layer-2 -mid"></span>
        <span class="btn-pill-bg-layer btn-pill-bg-layer-3 -base"></span>
      </span>
    </span>
    <span class="btn-pill-inner">
      <span class="btn-pill-inner-static">⋯</span>
      <span class="btn-pill-inner-hover" aria-hidden="true">⋯</span>
    </span>`;

  // Menu hors du shell (évite overflow:hidden)
  const menu = document.createElement('div');
  menu.className = 'btn-pill-group__menu';
  menu.setAttribute('role', 'menu');
  menu.hidden = true;

  overflowWrap.appendChild(moreBtn);
  group.appendChild(overflowWrap);
  document.body.appendChild(menu);

  let ignoreOutside = false;

  const positionMenu = (): void => {
    const rect = moreBtn.getBoundingClientRect();
    menu.style.top = `${Math.round(rect.bottom + 6)}px`;
    menu.style.left = 'auto';
    menu.style.right = `${Math.round(window.innerWidth - rect.right)}px`;
  };

  const closeMenu = (): void => {
    menu.hidden = true;
    overflowWrap.classList.remove('is-open');
    moreBtn.setAttribute('aria-expanded', 'false');
  };

  const openMenu = (): void => {
    positionMenu();
    menu.hidden = false;
    overflowWrap.classList.add('is-open');
    moreBtn.setAttribute('aria-expanded', 'true');
  };

  const onMoreClick = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    if (menu.hidden) {
      openMenu();
      // Le même clic remonte parfois jusqu’au document : ignorer une frame
      ignoreOutside = true;
      window.setTimeout(() => {
        ignoreOutside = false;
      }, 0);
    } else {
      closeMenu();
    }
  };

  const onOutsidePointer = (e: Event): void => {
    if (ignoreOutside) return;
    const t = e.target;
    if (!(t instanceof Node)) return;
    if (overflowWrap.contains(t) || menu.contains(t)) return;
    closeMenu();
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeMenu();
  };

  const onWinScroll = (): void => {
    if (!menu.hidden) positionMenu();
  };

  moreBtn.addEventListener('click', onMoreClick);
  document.addEventListener('pointerdown', onOutsidePointer, true);
  document.addEventListener('keydown', onKey);
  window.addEventListener('resize', onWinScroll);
  window.addEventListener('scroll', onWinScroll, true);

  let applying = false;
  const relayout = (): void => {
    applying = true;
    try {
      closeMenu();
      const pills = [...group.querySelectorAll<HTMLElement>(':scope > .btn-pill')].filter(
        isPillParticipating
      );
      for (const btn of pills) {
        btn.classList.remove('btn-pill-group__overflowed');
      }
      overflowWrap.hidden = true;

      const available = group.clientWidth;
      if (available <= 0 || pills.length === 0) {
        menu.replaceChildren();
        return;
      }

      const widths = pills.map((b) => b.getBoundingClientRect().width);
      overflowWrap.hidden = false;
      overflowWrap.style.visibility = 'hidden';
      const overflowW = moreBtn.getBoundingClientRect().width || 40;
      overflowWrap.style.visibility = '';
      overflowWrap.hidden = true;

      const pick = pickVisiblePillIndices(widths, available, overflowW);

      for (let i = 0; i < pills.length; i++) {
        pills[i]!.classList.toggle('btn-pill-group__overflowed', pick.overflow.includes(i));
      }

      menu.replaceChildren();
      if (pick.overflow.length === 0) {
        overflowWrap.hidden = true;
        return;
      }

      overflowWrap.hidden = false;
      for (const i of pick.overflow) {
        const src = pills[i]!;
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'btn-pill-group__menu-item';
        item.setAttribute('role', 'menuitem');
        item.textContent = pillLabel(src);
        if (src.dataset.role) item.dataset.role = src.dataset.role;
        item.addEventListener('click', () => {
          closeMenu();
          src.click();
        });
        menu.appendChild(item);
      }
    } finally {
      applying = false;
    }
  };

  let scheduled = false;
  const schedule = (): void => {
    if (applying || scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      relayout();
    });
  };

  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(schedule);
    ro.observe(group);
    if (group.parentElement) ro.observe(group.parentElement);
  } else {
    window.addEventListener('resize', schedule);
  }

  // applyRoleVisibility tourne juste après le montage : 2 frames pour décider
  schedule();
  window.setTimeout(schedule, 0);
  window.setTimeout(schedule, 50);

  return () => {
    ro?.disconnect();
    window.removeEventListener('resize', schedule);
    window.removeEventListener('resize', onWinScroll);
    window.removeEventListener('scroll', onWinScroll, true);
    moreBtn.removeEventListener('click', onMoreClick);
    document.removeEventListener('pointerdown', onOutsidePointer, true);
    document.removeEventListener('keydown', onKey);
    overflowWrap.remove();
    menu.remove();
    group.classList.remove('btn-pill-group--overflow');
    for (const btn of group.querySelectorAll<HTMLElement>('.btn-pill-group__overflowed')) {
      btn.classList.remove('btn-pill-group__overflowed');
    }
  };
}
